import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Logo } from '../Logo';
import { NotificationBadge } from '../NotificationBadge';
import {
  LayoutDashboard,
  AppWindow,
  Users,
  UserMinus,
  ClipboardCheck,
  LogOut,
  User,
  ListTodo,
  Settings,
  Building2,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  ScrollText,
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface MainLayoutProps {
  children: React.ReactNode;
  onHelpClick?: () => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  onHelpClick,
}) => {
  const { user, signOut, isSuperAdmin, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  const handleSignOut = async () => {
    setIsMobileMenuOpen(false);
    await signOut();
    navigate('/login');
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const navGroups = [
    {
      label: null,
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, superAdminOnly: false },
        { path: '/my-tasks', label: 'Tasks', icon: ListTodo, superAdminOnly: false },
        { path: '/reviews', label: 'Reviews', icon: ClipboardCheck, superAdminOnly: false },
        { path: '/offboarding', label: 'Offboarding', icon: UserMinus, superAdminOnly: true },
      ],
    },
    {
      label: 'Administration',
      items: [
        { path: '/applications', label: 'Apps', icon: AppWindow, superAdminOnly: true },
        { path: '/directions', label: 'Directions', icon: Building2, superAdminOnly: true },
        { path: '/users', label: 'Users', icon: Users, superAdminOnly: true },
      ],
    },
    {
      label: 'Configuration',
      items: [
        { path: '/settings', label: 'Settings', icon: Settings, superAdminOnly: true },
        { path: '/activity-logs', label: 'Activity Logs', icon: ScrollText, superAdminOnly: true },
      ],
    },
  ];

  const allNavItems = navGroups.flatMap(group => group.items);

  const getInitials = (email: string) => {
    const parts = email.split('@')[0].split('.');
    return parts.map(p => p[0].toUpperCase()).join('').slice(0, 2);
  };

  const getUserName = () => {
    if (!user?.email) return 'User';
    return user.email.split('@')[0].split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  };

  const getRoleLabel = () => {
    switch (role) {
      case 'superadmin': return 'Super Admin';
      case 'app_admin': return 'App Admin';
      case 'viewer': return 'Viewer';
      default: return 'User';
    }
  };

  const getRoleColor = () => {
    switch (role) {
      case 'superadmin': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
      case 'app_admin': return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
      case 'viewer': return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="px-5 py-5 border-b border-border">
        <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <Logo size="md" />
        </Link>
      </div>

      {/* User Profile */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600 text-white text-sm font-semibold flex-shrink-0">
            {user?.email ? getInitials(user.email) : <User className="w-4 h-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground truncate">{getUserName()}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleColor()}`}>
            {getRoleLabel()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="h-7 px-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            title="Sign Out"
          >
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        {navGroups.map((group, groupIndex) => {
          const visibleItems = group.items.filter(item => !item.superAdminOnly || isSuperAdmin);
          if (visibleItems.length === 0) return null;

          return (
            <div key={groupIndex} className={groupIndex > 0 ? 'mt-5' : ''}>
              {group.label && (
                <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
              )}
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleNavClick}
                      className={`
                        flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors
                        ${isActive
                          ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-semibold border-l-2 border-indigo-600 -ml-px'
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground font-medium'
                        }
                      `}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-indigo-600 dark:text-indigo-400' : ''}`} />
                      <span>{item.label}</span>
                      {item.path === '/my-tasks' && <NotificationBadge inline />}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Theme Toggle */}
      <div className="px-3 py-3 border-t border-border">
        <div className="flex items-center rounded-md bg-muted p-0.5">
          <button
            onClick={() => setTheme('light')}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
              theme === 'light'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Light"
          >
            <Sun className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setTheme('dark')}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
              theme === 'dark'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Dark"
          >
            <Moon className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setTheme('system')}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
              theme === 'system'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="System"
          >
            <Monitor className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-[260px] md:flex-shrink-0 bg-card border-r border-border fixed inset-y-0 left-0 z-30">
        {sidebarContent}
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center">
          <Logo size="sm" />
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="md:hidden fixed inset-y-0 left-0 z-50 w-[280px] bg-card flex flex-col shadow-xl">
            {sidebarContent}
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 md:ml-[260px] min-w-0 overflow-hidden">
        <main className="px-4 md:px-6 py-4 md:py-6 mt-14 md:mt-0 min-h-screen overflow-x-auto">
          {/* Breadcrumbs */}
          <div className="mb-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/dashboard" className="hover:text-foreground transition-colors">
                Home
              </Link>
              {location.pathname !== '/dashboard' && (
                <>
                  <span>/</span>
                  <span className="text-foreground font-medium">
                    {allNavItems.find(item => item.path === location.pathname)?.label || 'Page'}
                  </span>
                </>
              )}
            </div>
          </div>

          {children}
        </main>
      </div>
    </div>
  );
};
